/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import moment from 'moment';
import {
  arraySeriesDateTimeIndex,
  arraySeriesValueIndex,
  config,
  EnergyOptimizationDayType
} from '../../DashboardKPIWidgets/EnergyOptimization/constants';
import {
  IDataPoint,
  IValue,
  IArraySeries,
  IPoint,
  IPointSeries,
  StrategyStatus
} from '../../DashboardKPIWidgets/EnergyOptimization/EnergyOptimizationInterfaces';
import {
  IAny,
  IEmaasEnergyOptimizationComponentData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { TFunction } from '@scuf/localization/dist/types';
import descriptors from '../../../locales/en-US/descriptors';
import { MAGIC_VALUE } from './Constants';

interface IUnixTimestampValue {
  dataTimeStamp: number;
  value: number;
  properties?: Record<string, string>;
}

const dateTimeStampToUnixRecordCache: Record<string, number> = {};

const strategyStatusLabels: { [key: number]: string } = {
  0: 'STATUSNOTAVAILABLE',
  1: 'ON',
  2: 'EONotOptimizing',
  3: 'OFFPLANNEDOFFFORIMPROVINGBASELINE',
  4: 'SwitchedOfffromCloud',
  5: 'SwitchedOfffromSite'
};

const getStrategyStatusLabel = (t: TFunction, statusNumber: number) =>
  strategyStatusLabels[statusNumber]
    ? t(
        (descriptors.energyManagement as any)[
          strategyStatusLabels[statusNumber]
        ]
      )
    : '';

// prettier-ignore
// @ts-ignore
const pipe = <T, T1>(...fns: ((data: IAny) => IAny)[]) => (x: T): T1 => fns.reduce((v, f) => f(v), x);

const getUnixTimestampInMillisecondsWithoutTimeComponent = (
  timestamp: string = ''
): number => {
  if (!dateTimeStampToUnixRecordCache[timestamp]) {
    const unixTimestamp = moment(timestamp, 'YYYY-MM-DD').valueOf();
    dateTimeStampToUnixRecordCache[timestamp] = unixTimestamp;
  }
  return dateTimeStampToUnixRecordCache[timestamp];
};

const getUnixTimestampInMillisecondsWithTimeComponent = (
  timestamp: string = ''
): number => {
  if (!dateTimeStampToUnixRecordCache[timestamp]) {
    const unixTimestamp = moment(timestamp).valueOf();
    dateTimeStampToUnixRecordCache[timestamp] = unixTimestamp;
  }
  return dateTimeStampToUnixRecordCache[timestamp];
};
const filterBy = (data: IDataPoint[], filterString: string) =>
  data.filter((item) => item.name === filterString);
const getDataPointValues = (data: IDataPoint[], seriesName: string) => {
  const [dataPoint] = filterBy(data, seriesName);
  return (dataPoint && dataPoint.values) || [];
};

const convertToTimeSeriesDataObject = (data: IValue[]) =>
  data.map(
    ({ dataTimeStamp, value, properties }) =>
      ({
        dataTimeStamp: getUnixTimestampInMillisecondsWithoutTimeComponent(
          dataTimeStamp
        ),
        value,
        properties
      } as IUnixTimestampValue)
  );

const convertToTimeSeriesDataArray = (data: IValue[]) => {
  if (data?.length === 1) {
    const timeStamp = data[0]?.dataTimeStamp;
    return [
      [
        getUnixTimestampInMillisecondsWithoutTimeComponent(timeStamp),
        data[0].value
      ],
      [
        getUnixTimestampInMillisecondsWithTimeComponent(
          moment(timeStamp, 'YYYY-MM-DD')
            .add(1, 'day')
            .toISOString()
        ),
        data[0].value
      ]
    ];
  } else {
    return data?.map((item) => [
      getUnixTimestampInMillisecondsWithoutTimeComponent(item.dataTimeStamp),
      item.value
    ]);
  }
};

const getActualCostData = (data: IDataPoint[]) =>
  getDataPointValues(data, 'ActualCost');
const getSavingsCostData = (data: IDataPoint[]) =>
  getDataPointValues(data, 'AdjustmentSavings');
const getEmsopStatusData = (data: IDataPoint[]) =>
  getDataPointValues(data, 'IsEmsopActive');
const getDayTypeData = (data: IDataPoint[]) =>
  getDataPointValues(data, 'DayType');
const getEOStatusData = (data: IDataPoint[]) =>
  getDataPointValues(data, 'EOStatusRuntime');

const isEmsopInactive = (item: IValue) => item.value === 0;
const isDayTypeInactive = (item: IValue) =>
  item.value === EnergyOptimizationDayType.Inactive;
const isDayTypeExcluded = (item: IValue) =>
  item.value === EnergyOptimizationDayType.Excluded;
const filterValue = (
  data: IValue[],
  filterCondition: (item: IValue) => boolean
) => data.filter(filterCondition);

const shouldUseDayTypeData = (dataItems: IDataPoint[]) =>
  getDayTypeData(dataItems).length > 0;

const getInactiveDaysDataObjectByEmsopStatus = pipe<
  IDataPoint[],
  IUnixTimestampValue[]
>(
  getEmsopStatusData,
  (data) => filterValue(data, isEmsopInactive),
  convertToTimeSeriesDataObject
);

const getEOStatusDataObject = pipe<IDataPoint[], number[][]>(
  getEOStatusData,
  convertToTimeSeriesDataObject
);

const getInactiveDaysDataObjectByDayType = pipe<
  IDataPoint[],
  IUnixTimestampValue[]
>(
  getDayTypeData,
  (data) => filterValue(data, isDayTypeInactive),
  convertToTimeSeriesDataObject
);
const getExcludedDaysDataObjectByDayType = pipe<
  IDataPoint[],
  IUnixTimestampValue[]
>(
  getDayTypeData,
  (data) => filterValue(data, isDayTypeExcluded),
  convertToTimeSeriesDataObject
);

const mapChartPointWithCorrespondingEOStatus = (item: any) => {
  return {
    x: item.dataTimeStamp,
    y: item.properties
  } as IPoint;
};

const getEOStatusRunTime = (dataItems: IDataPoint[]): any => {
  return getEOStatusDataObject(dataItems).map((item) =>
    mapChartPointWithCorrespondingEOStatus(item)
  );
};

const getActualConsumptionChartData = pipe<IDataPoint[], number[][]>(
  getActualCostData,
  convertToTimeSeriesDataArray
);
const getEnergySavingsChartData = pipe<IDataPoint[], number[][]>(
  getSavingsCostData,
  convertToTimeSeriesDataArray
);

const toChartPointWithCorrespondingActualCost = (
  item: IUnixTimestampValue,
  actualConsumption: IArraySeries
) => {
  const [correspondingActualCost] = actualConsumption.filter(
    (point) => point[arraySeriesDateTimeIndex] === item.dataTimeStamp
  );
  return {
    x: item.dataTimeStamp,
    y: correspondingActualCost
      ? correspondingActualCost[arraySeriesValueIndex]
      : null,
    description: item.properties?.DayDetails
  } as IPoint;
};

const getInactiveDaysChartData = (dataItems: IDataPoint[]): IPointSeries => {
  const actualConsumption = getActualConsumptionChartData(dataItems);
  return (shouldUseDayTypeData(dataItems)
    ? getInactiveDaysDataObjectByDayType(dataItems)
    : getInactiveDaysDataObjectByEmsopStatus(dataItems)
  ).map((item) =>
    toChartPointWithCorrespondingActualCost(item, actualConsumption)
  );
};

const getExcludedDaysChartData = (dataItems: IDataPoint[]): IPointSeries => {
  const actualConsumption = getActualConsumptionChartData(dataItems);
  return (shouldUseDayTypeData(dataItems)
    ? getExcludedDaysDataObjectByDayType(dataItems)
    : []
  ).map((item) =>
    toChartPointWithCorrespondingActualCost(item, actualConsumption)
  );
};

const getUnit = (dataItems?: IDataPoint[]): string => {
  const [dataPoint] =
    dataItems?.filter((item) => item.name === 'ActualCost') || [];
  return (dataPoint && dataPoint.unit) || config.defaultCurrencyName;
};

const eoLastStatusMapper: { [key: number]: string } = {
  0: 'STATUSNOTAVAILABLE',
  1: 'ON',
  2: 'EONotOptimizing',
  3: 'OFFPLANNEDOFFFORIMPROVINGBASELINE',
  4: 'SwitchedOfffromCloud',
  5: 'SwitchedOfffromSite'
};

const eoStatusTooltipText: { [key: number]: string } = {
  0: 'STATUSNOTAVAILABLETOOLTIP',
  1: 'EOON',
  2: 'EOOFFLINE',
  3: 'PLANNEDOFFFORBASELINE',
  4: 'SwitchedOfffromCloudToolTip',
  5: 'SwitchedOfffromSiiteToolTip'
};

export const getEOLastTooltip = (response: number): string =>
  eoStatusTooltipText[response];

export const getEoLastStatus = (response: number): string =>
  eoLastStatusMapper[response];

const getEmsopStrategyStatus = (
  response: IEmaasEnergyOptimizationComponentData
): StrategyStatus =>
  response.currentState && response.currentState.state === 1
    ? 'active'
    : 'inactive';

const getStrategyLastUpdated = (
  response: IEmaasEnergyOptimizationComponentData
): moment.Moment | undefined =>
  response.currentState && response.currentState.timestamp
    ? moment(response.currentState.timestamp)
    : undefined;

const getStrategyLastUpdatedForEOStatus = (
  data: IAny
): moment.Moment | undefined => moment(data);

const convertActiveDurationToHrs = (data: number[][]) =>
  data.map((item) => [item[0], item[1] / MAGIC_VALUE.THREESIXZEROZERO]);
const getActiveDurationChartData = pipe<IDataPoint[], number[][]>(
  (data: IDataPoint[]) => getDataPointValues(data, 'ActiveDuration'),
  convertToTimeSeriesDataArray,
  convertActiveDurationToHrs
);

export {
  getActiveDurationChartData,
  getActualConsumptionChartData,
  getEmsopStrategyStatus,
  getStrategyLastUpdated,
  getEnergySavingsChartData,
  getInactiveDaysChartData,
  getExcludedDaysChartData,
  getEOStatusRunTime,
  getUnit,
  shouldUseDayTypeData,
  getUnixTimestampInMillisecondsWithoutTimeComponent,
  getUnixTimestampInMillisecondsWithTimeComponent,
  pipe,
  getStrategyStatusLabel,
  getStrategyLastUpdatedForEOStatus
};
